import React, { useEffect, useState } from 'react';
import { PageContent, PageElement, TextLarge, Icon, TIcon } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-internal/js-react-i18n';

import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import { VEHICLE_TYPE_EV, VEHICLE_TYPE_CATEGORY_IDS_NUM, VEHICLE_TYPES } from '../../../../constants/vehicleTypes';
import { trackGAEvent } from '../../utils/gaTracking';
import { ATTRIBUTE_VALUES } from '../../../../constants/attributes';
import getFavorites from '../../repositories/getFavorites';
import deleteFavorite from '../../repositories/deleteFavorite';
import createFavorite from '../../repositories/createFavorite';

import BannerContainer from '../Banners/Banners';
import FeedsApp from '../FeedsApp/FeedsApp';
import CarsSearchApp from '../CarsSearch/CarsSearch';
import RecentlyViewedList from '../Motors/components/RecentlyViewedList';
import ContentBlocks from './components/ContentBlocks';
import ContentArticles from '../ContentArticles/components/ContentArticles';
import FaqList from '../FaqList/FaqList';
import FavoritesList from '../Motors/components/FavoritesList';
import HubList from './components/HubList';

import Classes from './components/CarsPageContent.module.scss';
import { TFavoriteAd } from 'types/TFavoriteAds';
import { TCarouselListingItem } from 'types/TCarousels';
import { TEnvConfigCars } from 'types/TServerContext';
import BuyerInfoModal from '../FavouritesConnect/BuyerInfoModal';
type TCarsPageContentAppProps = {
  environment: TEnvConfigCars;
  appState: any;
};

const USP_ICONS: Record<string, TIcon[]> = {
  buy: ['dealers', 'clock'],
  lease: ['dealers'],
  sell: ['profile'],
};

const CarsPageContentApp = ({ environment, appState }: TCarsPageContentAppProps) => {
  const { t } = useI18nContext();
  const [vehicleType, setVehicleType] = useState(environment.pageVehicleType);
  const { isFavouritesConnectEnabled } = environment.experiments;
  const { tenant } = environment.tenantContext;
  const [favoriteItems, setFavoriteItems] = useState<TCarouselListingItem[]>([]);
  const [relevantFavoriteItems, setRelevantFavoriteItems] = useState<TCarouselListingItem[]>([]);
  const [showFavouriteInfoModal, setShowFavouriteInfoModal] = useState(false);
  const needsBasedContent = appState.contentArticles.cars.find(({ key }) => key === 'needs_based_search');
  const intent = environment.intent as string;
  const isLeasePage = intent === 'lease';
  const heroLabelKey = !isLeasePage && vehicleType === VEHICLE_TYPE_EV ? `page.title.ev` : `page.title.${intent}`;

  useEffect(() => {
    window.addEventListener('vehicle-type-changed', (event) => {
      setVehicleType((event as CustomEvent).detail);
    });

    window.addEventListener('placead', (_event) => {
      trackGAEvent('placeAdButtonClicked', 'header');
    });
  }, []);

  useEffect(() => {
    if (!environment.userLoggedIn) {
      return;
    }

    void (async () => {
      let favoriteAds: TFavoriteAd[] = [];
      try {
        favoriteAds = await getFavorites();
      } catch {}

      if (!favoriteAds.length) {
        return;
      }

      setFavoriteItems(favoriteAds);
    })();
  }, [environment.userLoggedIn]);

  useEffect(() => {
    setRelevantFavoriteItems(
      favoriteItems.filter((fav) => {
        const matchesL2Category =
          vehicleType === VEHICLE_TYPES.CARS
            ? !Object.values(VEHICLE_TYPE_CATEGORY_IDS_NUM).includes(fav.l2CategoryId)
            : fav.l2CategoryId === VEHICLE_TYPE_CATEGORY_IDS_NUM[vehicleType];
        const matchesPriceType = !isLeasePage ? !fav.isPricePerMonth : fav.isPricePerMonth;
        return matchesL2Category && matchesPriceType;
      }),
    );
  }, [favoriteItems, vehicleType, isLeasePage]);

  const onFavClickHandler = async (item: TCarouselListingItem, isFavorite: boolean) => {
    if (isFavorite) {
      trackGAEvent('WatchlistRemoveBegin');
      setFavoriteItems(favoriteItems.filter((ad) => ad.itemId !== item.itemId));
      await deleteFavorite(item.itemId, environment.xsrfToken);
      trackGAEvent('WatchlistRemoveSuccess');
    } else {
      trackGAEvent('WatchlistAddBegin');
      setFavoriteItems([item, ...favoriteItems]);
      await createFavorite(item.itemId, environment.xsrfToken);
      trackGAEvent('WatchlistAddSuccess', undefined, undefined, {
        // eslint-disable-next-line camelcase
        seller_contact_enabled: environment.favouritesConnect?.sellerContactEnabled,
      });

      setShowFavouriteInfoModal(true);
    }
  };

  return (
    <EnvironmentProvider value={environment}>
      <PageElement className={Classes.heroSection}>
        <PageContent className={Classes.hero}>
          <div className={Classes.heroContent}>
            <h1 className={Classes.pageTitle}>
              <span className={Classes.heroHeaderLine}>{t(`${heroLabelKey}.line1`)}</span>
              <span className={Classes.heroHeaderLine}>{t(`${heroLabelKey}.line2`)}</span>
            </h1>
            <TextLarge as="h2">{t(`page.subtitle.${intent}`)}</TextLarge>
            <ul className={Classes.heroUsps}>
              <li className={Classes.heroUsp}>
                <Icon className={Classes.heroUspIcon} name={USP_ICONS[intent][0]} color="text-brand-primary" />
                <Trans
                  tagName="span"
                  i18nKey={appState.usps[0].key}
                  count={appState.usps[!isLeasePage && vehicleType === VEHICLE_TYPE_EV ? 2 : 0].count}
                />
              </li>
              {appState.usps[1] && vehicleType !== VEHICLE_TYPE_EV && (
                <li className={Classes.heroUsp}>
                  <Icon className={Classes.heroUspIcon} name={USP_ICONS[intent][1]} color="text-brand-primary" />
                  <Trans tagName="span" i18nKey={appState.usps[1].key} />
                </li>
              )}
            </ul>
          </div>
          <CarsSearchApp
            l1Category={appState.l1Category}
            carsSearchFields={appState.carsSearchFields}
            userPostcode={appState.userPostcode}
            vehicleType={vehicleType}
            priceType={intent === 'buy' ? ATTRIBUTE_VALUES.PRICE_TYPE.BUY : ATTRIBUTE_VALUES.PRICE_TYPE.LEASE}
            intent={intent}
          />
        </PageContent>
      </PageElement>
      {vehicleType === VEHICLE_TYPE_EV ? (
        <PageElement>
          <PageContent className={Classes.carouselSection}>
            <ContentArticles contentArticles={appState.contentArticles.evs} isLeasePage={isLeasePage} />
            <section className="faqSection PageContent-offset">
              <FaqList />
            </section>
          </PageContent>
        </PageElement>
      ) : (
        <PageElement>
          <PageContent className={Classes.carouselSection}>
            {Boolean(relevantFavoriteItems.length) && (
              <FavoritesList
                title={t(`cars.carousel.title.user_favorites.${vehicleType}`)}
                favoriteAdItems={relevantFavoriteItems}
                onFavClickHandler={onFavClickHandler}
              />
            )}
            <RecentlyViewedList
              favoriteAdItems={relevantFavoriteItems}
              onFavClickHandler={onFavClickHandler}
              vehicleType={vehicleType}
              isLease={isLeasePage}
            />
            {vehicleType === VEHICLE_TYPES.CARS && <HubList content={needsBasedContent} isLeasePage={isLeasePage} />}
            {appState.withBanners && (
              <section className="Page-section Page-section--full-width bannerSection" id="banner-root">
                <BannerContainer id="banner-top-dt" environment={environment} l1Category={appState.l1Category} />
              </section>
            )}
            <section className="Page-section Page-section--full-width" id="feeds-root">
              <FeedsApp
                appState={appState}
                environment={environment}
                favoriteAdItems={relevantFavoriteItems}
                onFavClickHandler={onFavClickHandler}
                asCarousels
                vehicleType={vehicleType}
                isLease={isLeasePage}
              />
            </section>
            {appState.withContentBlocks && <ContentBlocks contentArticles={appState.contentArticles.cars} />}
          </PageContent>
        </PageElement>
      )}
      {isFavouritesConnectEnabled && showFavouriteInfoModal && (
        <BuyerInfoModal
          onClose={() => setShowFavouriteInfoModal(false)}
          onTrack={(params) => {
            trackGAEvent(params.action, params.label, null, {
              // eslint-disable-next-line camelcase
              event_category: params.category,
            });
          }}
        />
      )}
    </EnvironmentProvider>
  );
};

export default CarsPageContentApp;
